import { useState } from 'react';
import { Card, Badge, Skeleton } from '@lineageatlas/design-system';
import { useApi } from '../hooks/useApi';
import type { ReferenceProfile, CellType } from '@lineageatlas/shared';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function expressionVariant(
  exp: ReferenceProfile['expected_expression'],
): 'excellent' | 'good' | 'caution' | 'risk' {
  switch (exp) {
    case 'positive':
      return 'excellent';
    case 'negative':
      return 'risk';
    default:
      return 'caution';
  }
}

function expressionLabel(exp: ReferenceProfile['expected_expression']): string {
  switch (exp) {
    case 'positive':
      return 'Positive';
    case 'negative':
      return 'Negative';
    default:
      return 'Variable';
  }
}

// ─── Responsive styles ────────────────────────────────────────────────────────

const styles = `
  .ref-table { display: none; }
  .ref-cards { display: grid; }
  @media (min-width: 640px) {
    .ref-table { display: table; }
    .ref-cards { display: none; }
  }
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ReferencesSkeleton() {
  return (
    <div>
      {/* Tabs skeleton */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} width="72px" height="34px" />
        ))}
      </div>

      <Card>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 'var(--space-4)',
              padding: 'var(--space-3) 0',
              borderBottom: i < 5 ? '1px solid var(--color-border)' : 'none',
            }}
          >
            <Skeleton width="15%" height="16px" />
            <Skeleton width="15%" height="16px" />
            <Skeleton width="12%" height="16px" />
            <Skeleton width="12%" height="16px" />
            <Skeleton width="20%" height="16px" />
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── Source citations ─────────────────────────────────────────────────────────

function SourceCitations({ profiles }: { profiles: ReferenceProfile[] }) {
  const sources = Array.from(
    new Map(
      profiles
        .filter((p) => p.source)
        .map((p) => [p.source!, { source: p.source!, detail: p.source_detail }]),
    ).values(),
  );

  if (sources.length === 0) return null;

  return (
    <div style={{ marginTop: 'var(--space-6)' }}>
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-h4)',
          fontWeight: 600,
          color: 'var(--color-text)',
          margin: '0 0 var(--space-3)',
        }}
      >
        Sources
      </h3>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {sources.map(({ source, detail }) => (
          <li
            key={source}
            style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-muted)' }}
          >
            <strong style={{ color: 'var(--color-text)', fontWeight: 600 }}>{source}</strong>
            {detail && <span style={{ marginLeft: 'var(--space-2)' }}>— {detail}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const DISPLAY_CELL_TYPES: CellType[] = ['UC-MSC', 'BM-MSC', 'AD-MSC', 'HSC', 'iPSC'];

export function ReferencesPage() {
  const [activeTab, setActiveTab] = useState<CellType>('UC-MSC');

  const {
    data: refData,
    loading,
    error,
  } = useApi<{ cell_type: string; markers: ReferenceProfile[] }>(`/api/v1/references/${activeTab}`);
  const profiles = refData?.markers ?? [];

  return (
    <>
      <style>{styles}</style>

      <div>
        {/* Page header */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-h1)',
              fontWeight: 600,
              letterSpacing: 'var(--tracking-heading)',
              lineHeight: 'var(--leading-heading)',
              color: 'var(--color-text)',
              margin: '0 0 var(--space-2)',
            }}
          >
            Reference Profiles
          </h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-body)', margin: 0 }}>
            Expected marker expression ranges used for QC scoring and drift detection.
          </p>
        </div>

        {/* Cell type tabs */}
        <div
          role="tablist"
          aria-label="Cell type"
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            marginBottom: 'var(--space-6)',
            flexWrap: 'wrap',
            borderBottom: '2px solid var(--color-border)',
            paddingBottom: 0,
          }}
        >
          {DISPLAY_CELL_TYPES.map((ct) => {
            const isActive = ct === activeTab;
            return (
              <button
                key={ct}
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-${ct}`}
                id={`tab-${ct}`}
                onClick={() => setActiveTab(ct)}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  border: 'none',
                  borderBottom: isActive
                    ? '2px solid var(--color-accent-primary, #6d28d9)'
                    : '2px solid transparent',
                  marginBottom: '-2px',
                  background: 'transparent',
                  color: isActive
                    ? 'var(--color-accent-primary, #6d28d9)'
                    : 'var(--color-text-muted)',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 'var(--text-body)',
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                  transition: 'color 0.15s ease, border-color 0.15s ease',
                }}
              >
                {ct}
              </button>
            );
          })}
        </div>

        {/* Tab panel */}
        <div role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
          {loading ? (
            <ReferencesSkeleton />
          ) : error ? (
            <Card>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--text-body)',
                  margin: 0,
                  textAlign: 'center',
                  padding: 'var(--space-8) 0',
                }}
              >
                Unable to load reference profiles for {activeTab}.
              </p>
            </Card>
          ) : !profiles || profiles.length === 0 ? (
            <Card>
              <p
                style={{
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--text-body)',
                  margin: 0,
                  textAlign: 'center',
                  padding: 'var(--space-10) 0',
                }}
              >
                No reference profile defined for {activeTab}.
              </p>
            </Card>
          ) : (
            <>
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                {/* Desktop table */}
                <table
                  className="ref-table"
                  style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
                  aria-label={`${activeTab} reference markers`}
                >
                  <thead>
                    <tr
                      style={{
                        background: 'var(--color-bg-subtle, var(--color-bg-surface))',
                        borderBottom: '2px solid var(--color-border)',
                      }}
                    >
                      {(
                        [
                          'Marker',
                          'Expression',
                          'Min Threshold',
                          'Max Threshold',
                          'Source',
                        ] as const
                      ).map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            textAlign: 'left',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 600,
                            color: 'var(--color-text-muted)',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map((p, idx) => (
                      <tr
                        key={p.id}
                        style={{
                          borderBottom:
                            idx < profiles.length - 1 ? '1px solid var(--color-border)' : 'none',
                        }}
                      >
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            fontSize: 'var(--text-body)',
                            color: 'var(--color-text)',
                          }}
                        >
                          {p.marker_name}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <Badge status={expressionVariant(p.expected_expression)}>
                            {expressionLabel(p.expected_expression)}
                          </Badge>
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontFamily: 'var(--font-mono)',
                            fontFeatureSettings: '"tnum"',
                            fontSize: 'var(--text-body)',
                            color:
                              p.expected_min != null
                                ? 'var(--color-text)'
                                : 'var(--color-text-muted)',
                          }}
                        >
                          {p.expected_min != null ? `${p.expected_min}%` : '—'}
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontFamily: 'var(--font-mono)',
                            fontFeatureSettings: '"tnum"',
                            fontSize: 'var(--text-body)',
                            color:
                              p.expected_max != null
                                ? 'var(--color-text)'
                                : 'var(--color-text-muted)',
                          }}
                        >
                          {p.expected_max != null ? `${p.expected_max}%` : '—'}
                        </td>
                        <td
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-caption)',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          {p.source ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <div className="ref-cards" style={{ gridTemplateColumns: '1fr', gap: 0 }}>
                  {profiles.map((p, idx) => (
                    <div
                      key={p.id}
                      style={{
                        padding: 'var(--space-4)',
                        borderBottom:
                          idx < profiles.length - 1 ? '1px solid var(--color-border)' : 'none',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 'var(--space-2)',
                          marginBottom: 'var(--space-2)',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            fontSize: 'var(--text-body)',
                            color: 'var(--color-text)',
                          }}
                        >
                          {p.marker_name}
                        </span>
                        <Badge status={expressionVariant(p.expected_expression)}>
                          {expressionLabel(p.expected_expression)}
                        </Badge>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'var(--space-3)',
                          fontSize: 'var(--text-caption)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {p.expected_min != null && (
                          <span>
                            Min:{' '}
                            <span
                              style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}
                            >
                              {p.expected_min}%
                            </span>
                          </span>
                        )}
                        {p.expected_max != null && (
                          <span>
                            Max:{' '}
                            <span
                              style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}
                            >
                              {p.expected_max}%
                            </span>
                          </span>
                        )}
                        {p.source && <span>Source: {p.source}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Source citations */}
              <SourceCitations profiles={profiles} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
